import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const STATUSES = ["Pending", "Active", "In Review", "Revision", "Completed", "On Hold", "Cancelled"];

export default function AdminProjects() {
  const qc = useQueryClient();
  const { data: projects = [] } = useQuery({ queryKey: ["admin-projects"], queryFn: async () => (await api.get("/admin/projects")).data });
  const { data: users = [] } = useQuery({ queryKey: ["admin-users", "client"], queryFn: async () => (await api.get("/admin/users", { params: { role: "client" } })).data });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const openNew = () => { setEditing(null); setForm({ status: "Pending", priority: "Medium", progress: 0 }); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...p }); setOpen(true); };
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = { ...form, progress: Number(form.progress || 0), budget: form.budget ? Number(form.budget) : undefined };
    if (payload.client_user_id) {
      const u = users.find((x) => x.user_id === payload.client_user_id);
      if (u) payload.client_name = u.name;
    }
    try {
      if (editing) await api.put(`/admin/projects/${editing.id}`, payload);
      else await api.post("/admin/projects", payload);
      toast.success("Project saved");
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["admin-projects"] });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete project?")) return;
    await api.delete(`/admin/projects/${id}`); toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["admin-projects"] });
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h1 className="font-display text-2xl font-bold">Projects</h1><p className="text-sm text-muted-foreground">{projects.length} projects</p></div>
        <Button className="rounded-full" onClick={openNew} data-testid="project-new"><Plus className="mr-2 h-4 w-4" /> New project</Button>
      </div>

      <div className="mt-6 grid gap-4 md:grid-cols-2">
        {projects.map((p) => (
          <div key={p.id} className="bs-card p-5" data-testid={`admin-project-${p.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div><h3 className="font-display font-bold">{p.title}</h3><p className="text-xs text-muted-foreground">{p.client_name || "Unassigned"}</p></div>
              <Badge variant="secondary">{p.status}</Badge>
            </div>
            <div className="mt-3 flex items-center gap-3"><Progress value={p.progress || 0} className="h-2" /><span className="text-xs">{p.progress || 0}%</span></div>
            <div className="mt-4 flex gap-2">
              <Button size="sm" variant="outline" onClick={() => openEdit(p)}>Edit</Button>
              <Button size="sm" variant="ghost" onClick={() => del(p.id)} className="text-destructive">Delete</Button>
            </div>
          </div>
        ))}
        {projects.length === 0 && <p className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground md:col-span-2">No projects yet.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit" : "New"} project</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3.5" data-testid="project-form">
            <div><Label className="text-xs">Title</Label><Input required value={form.title ?? ""} onChange={(e) => upd("title", e.target.value)} className="mt-1" /></div>
            <div><Label className="text-xs">Description</Label><Textarea rows={3} value={form.description ?? ""} onChange={(e) => upd("description", e.target.value)} className="mt-1" /></div>
            <div><Label className="text-xs">Client</Label>
              <Select value={form.client_user_id ?? ""} onValueChange={(v) => upd("client_user_id", v)}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Assign a client" /></SelectTrigger>
                <SelectContent>{users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name} ({u.email})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Status</Label>
                <Select value={form.status} onValueChange={(v) => upd("status", v)}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><Label className="text-xs">Priority</Label>
                <Select value={form.priority} onValueChange={(v) => upd("priority", v)}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{["Low", "Medium", "High", "Urgent"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><Label className="text-xs">Progress %</Label><Input type="number" min={0} max={100} value={form.progress ?? 0} onChange={(e) => upd("progress", e.target.value)} className="mt-1" /></div>
              <div><Label className="text-xs">Budget</Label><Input type="number" value={form.budget ?? ""} onChange={(e) => upd("budget", e.target.value)} className="mt-1" /></div>
            </div>
            <DialogFooter><Button type="submit" className="rounded-full" disabled={busy} data-testid="project-save">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
